import { useMemo, useState } from 'react';
import { ConfigurationMapResponse } from '@rossum/api-client/internal';
import useDebounce from '../../../utils/hooks/useDebounce';
import { filterConfigMapData } from './filterConfigMapData';
import type { SearchOption } from './SearchOption';

type ConfigMapData = ConfigurationMapResponse['results'];

/**
 * Encapsulates search state for the config map: chip selection, input text,
 * filtered options for the dropdown, and the filtered data result.
 */
export const useConfigMapSearch = (data: ConfigMapData | undefined) => {
  const [searchChips, setSearchChips] = useState<SearchOption[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const debouncedInput = useDebounce(searchInput);

  const searchOptions = useMemo<SearchOption[]>(
    () =>
      data
        ? [
            ...data.fields.map(f => ({
              id: f.id,
              label: f.id,
              type: f.type,
            })),
            ...data.rules.map(r => ({
              id: r.id,
              label: r.name,
              type: 'rule' as const,
            })),
            ...data.hooks.map(h => ({
              id: h.id,
              label: h.name,
              type: h.hookType,
            })),
          ]
        : [],
    [data]
  );

  const filteredSearchOptions = useMemo(() => {
    const input = debouncedInput.toLowerCase().trim();
    if (!input) return searchOptions;
    return searchOptions.filter(
      opt =>
        opt.label.toLowerCase().includes(input) ||
        opt.id.toLowerCase().includes(input)
    );
  }, [searchOptions, debouncedInput]);

  const filteredData = useMemo(() => {
    if (!data) return null;
    return filterConfigMapData(
      data.fields,
      data.edges,
      data.rules,
      data.hooks,
      searchChips.map(c => c.id)
    );
  }, [data, searchChips]);

  return {
    searchChips,
    setSearchChips,
    searchInput,
    setSearchInput,
    filteredSearchOptions,
    filteredData,
  };
};
